/**
 * SignageEditor.js
 *
 * Dev-only editor for placing and configuring ground navigation signages
 * in the Matterport 3D scene. Gated by REACT_APP_SIGNAGE_EDITOR=true.
 *
 * Pattern follows ZoneDrawingTool.js — React + inline styles, click-to-place,
 * language tabs, clipboard JSON export.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import signageService from '../services/signageService';
import { Z_INDEX } from '../const';

const PANEL_WIDTH = 340;
const SIGNAGE_TYPES = [
  { value: 'arrow', label: '➡️ Arrow' },
  { value: 'text', label: '📝 Text' },
  { value: 'arrow_text', label: '➡️📝 Arrow + Text' },
];
const LANGUAGES = ['mr', 'en', 'hi'];
const LANG_LABELS = { mr: 'मराठी', en: 'English', hi: 'हिंदी' };

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = {
  toggleBtn: {
    position: 'fixed',
    bottom: 80,
    left: 16,
    zIndex: Z_INDEX.SIGNAGE_EDITOR,
    width: 44,
    height: 44,
    borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.3)',
    background: 'rgba(0,0,0,0.7)',
    color: '#FFD700',
    fontSize: 20,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(8px)',
  },
  panel: {
    position: 'fixed',
    top: 60,
    left: 16,
    width: PANEL_WIDTH,
    maxHeight: 'calc(100vh - 80px)',
    overflowY: 'auto',
    zIndex: Z_INDEX.SIGNAGE_EDITOR,
    background: 'rgba(15,15,15,0.95)',
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.1)',
    color: '#e0e0e0',
    fontSize: 13,
    fontFamily: 'system-ui, sans-serif',
    backdropFilter: 'blur(12px)',
    padding: 12,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  title: {
    fontSize: 15,
    fontWeight: 700,
    color: '#FFD700',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#999',
    fontSize: 18,
    cursor: 'pointer',
    padding: '2px 6px',
  },
  btn: {
    padding: '6px 12px',
    borderRadius: 6,
    border: 'none',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
  },
  btnPrimary: {
    background: '#FFD700',
    color: '#000',
  },
  btnDanger: {
    background: '#dc3545',
    color: '#fff',
  },
  btnSecondary: {
    background: 'rgba(255,255,255,0.1)',
    color: '#e0e0e0',
  },
  separator: {
    borderTop: '1px solid rgba(255,255,255,0.1)',
    margin: '8px 0',
  },
  label: {
    display: 'block',
    fontSize: 11,
    color: '#999',
    marginBottom: 3,
    marginTop: 8,
  },
  input: {
    width: '100%',
    padding: '5px 8px',
    borderRadius: 4,
    border: '1px solid rgba(255,255,255,0.15)',
    background: 'rgba(255,255,255,0.06)',
    color: '#e0e0e0',
    fontSize: 12,
    outline: 'none',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '5px 8px',
    borderRadius: 4,
    border: '1px solid rgba(255,255,255,0.15)',
    background: 'rgba(30,30,30,0.9)',
    color: '#e0e0e0',
    fontSize: 12,
    outline: 'none',
  },
  row: {
    display: 'flex',
    gap: 6,
    alignItems: 'center',
  },
  signageItem: {
    padding: '8px 10px',
    marginBottom: 6,
    borderRadius: 6,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    cursor: 'pointer',
  },
  signageItemActive: {
    border: '1px solid #FFD700',
    background: 'rgba(255, 215, 0, 0.08)',
  },
  langTab: {
    padding: '3px 8px',
    borderRadius: 4,
    border: '1px solid rgba(255,255,255,0.15)',
    background: 'transparent',
    color: '#999',
    fontSize: 11,
    cursor: 'pointer',
  },
  langTabActive: {
    background: '#FFD700',
    color: '#000',
    borderColor: '#FFD700',
  },
  toast: {
    position: 'fixed',
    bottom: 20,
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#FFD700',
    color: '#000',
    padding: '8px 20px',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 13,
    zIndex: Z_INDEX.SIGNAGE_EDITOR + 1,
    pointerEvents: 'none',
  },
  addModeBar: {
    padding: '8px 12px',
    margin: '8px 0',
    borderRadius: 6,
    background: 'rgba(255,215,0,0.15)',
    border: '1px solid #FFD700',
    textAlign: 'center',
    color: '#FFD700',
    fontSize: 12,
    fontWeight: 600,
  },
};

// ─── Component ───────────────────────────────────────────────────────────────

const SignageEditor = () => {
  const [open, setOpen] = useState(false);
  const [addMode, setAddMode] = useState(false);
  const [relocateMode, setRelocateMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [signages, setSignages] = useState([]);
  const [editLang, setEditLang] = useState('en');
  const [toast, setToast] = useState(null);
  const iframeRef = useRef(null);

  // Sync signages list from service
  const refreshList = useCallback(() => {
    setSignages([...signageService.getSignages()]);
  }, []);

  useEffect(() => {
    refreshList();
  }, [open, refreshList]);

  // Find the Matterport iframe for click-to-place
  useEffect(() => {
    const iframe = document.querySelector('iframe[title="Matterport"]');
    iframeRef.current = iframe;
  }, [open]);

  // Click-to-place handler
  useEffect(() => {
    if (!addMode) return;

    const sdk = signageService.getSDK();
    if (!sdk) return;

    const handleClick = async (e) => {
      // Use Renderer intersection from SDK
      try {
        const rect = iframeRef.current?.getBoundingClientRect();
        if (!rect) return;

        // We need to intercept clicks on the iframe — use pointer from document
        const posData = await sdk.Renderer.getWorldPositionData({
          x: e.clientX - (rect?.left || 0),
          y: e.clientY - (rect?.top || 0),
        });

        if (!posData?.position) {
          console.warn('🪧 Editor: no intersection point');
          return;
        }

        const { x, z } = posData.position;
        const newSignage = {
          type: 'arrow_text',
          label: { en: 'New Sign', mr: 'नवीन चिन्ह', hi: 'नया चिन्ह' },
          position: { x: parseFloat(x.toFixed(3)), y: 0.05, z: parseFloat(z.toFixed(3)) },
          rotation: { y: 0 },
          scale: 1.0,
          arrowColor: '#FFD700',
          textColor: '#FFFFFF',
          bgColor: '#000000',
          bgOpacity: 0.7,
          visible: true,
        };

        const assignedId = await signageService.addSignage(newSignage);
        setAddMode(false);
        setSelectedId(assignedId);
        refreshList();
        showToast('Signage placed!');
      } catch (err) {
        console.error('🪧 Editor: placement failed:', err);
      }
    };

    // Listen on the iframe's content window if accessible, else parent window
    const target = iframeRef.current?.contentWindow?.document || document;
    target.addEventListener('click', handleClick);
    return () => target.removeEventListener('click', handleClick);
  }, [addMode, refreshList]);

  // Click-to-relocate handler
  useEffect(() => {
    if (!relocateMode || !selectedId) return;

    const sdk = signageService.getSDK();
    if (!sdk) return;

    const handleClick = async (e) => {
      try {
        const rect = iframeRef.current?.getBoundingClientRect();
        if (!rect) return;

        const posData = await sdk.Renderer.getWorldPositionData({
          x: e.clientX - (rect?.left || 0),
          y: e.clientY - (rect?.top || 0),
        });

        if (!posData?.position) return;

        const { x, z } = posData.position;
        signageService.updateSignage(selectedId, {
          position: {
            x: parseFloat(x.toFixed(3)),
            z: parseFloat(z.toFixed(3)),
          },
        });
        setRelocateMode(false);
        refreshList();
        showToast('Signage repositioned!');
      } catch (err) {
        console.error('🪧 Editor: relocate failed:', err);
      }
    };

    const target = iframeRef.current?.contentWindow?.document || document;
    target.addEventListener('click', handleClick);
    return () => target.removeEventListener('click', handleClick);
  }, [relocateMode, selectedId, refreshList]);

  // Keyboard shortcut: Ctrl+Shift+S to toggle
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'S') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2000);
  };

  const selectedSignage = signages.find(s => s.id === selectedId);

  const handleUpdate = (field, value) => {
    if (!selectedId) return;
    const updates = {};

    if (field.startsWith('position.')) {
      const axis = field.split('.')[1];
      updates.position = { [axis]: parseFloat(value) || 0 };
    } else if (field.startsWith('rotation.')) {
      const axis = field.split('.')[1];
      updates.rotation = { [axis]: parseFloat(value) || 0 };
    } else if (field.startsWith('label.')) {
      const lang = field.split('.')[1];
      updates.label = { [lang]: value };
    } else {
      updates[field] = value;
    }

    signageService.updateSignage(selectedId, updates);
    refreshList();
  };

  const handleDelete = () => {
    if (!selectedId) return;
    signageService.removeSignage(selectedId);
    setSelectedId(null);
    refreshList();
    showToast('Signage deleted');
  };

  const handleExport = async () => {
    const json = signageService.exportConfig();
    try {
      await navigator.clipboard.writeText(json);
      showToast('Config JSON copied to clipboard!');
    } catch {
      console.log(json);
      showToast('JSON logged to console');
    }
  };

  if (!open) {
    return (
      <button
        style={styles.toggleBtn}
        onClick={() => setOpen(true)}
        title="Signage Editor (Ctrl+Shift+S)"
      >
        🪧
      </button>
    );
  }

  return (
    <>
      <div style={styles.panel}>
        {/* Header */}
        <div style={styles.header}>
          <h3 style={styles.title}>🪧 Signage Editor</h3>
          <button style={styles.closeBtn} onClick={() => { setOpen(false); setAddMode(false); setRelocateMode(false); }}>✕</button>
        </div>

        {/* Action Bar */}
        <div style={{ ...styles.row, marginBottom: 8 }}>
          <button
            style={{ ...styles.btn, ...styles.btnPrimary, flex: 1 }}
            onClick={() => setAddMode(!addMode)}
          >
            {addMode ? '✕ Cancel' : '+ Add Signage'}
          </button>
          <button
            style={{ ...styles.btn, ...styles.btnSecondary }}
            onClick={handleExport}
            title="Copy config JSON to clipboard"
          >
            📋 Export
          </button>
        </div>

        {addMode && (
          <div style={styles.addModeBar}>
            Click on the ground in the 3D view to place a signage
          </div>
        )}

        {relocateMode && (
          <div style={{ ...styles.addModeBar, background: 'rgba(0,150,255,0.15)', borderColor: '#4da6ff', color: '#4da6ff' }}>
            Click on the ground to move the selected signage
            <button
              style={{ ...styles.btn, marginLeft: 8, background: 'rgba(255,255,255,0.15)', color: '#4da6ff', fontSize: 11, padding: '3px 8px' }}
              onClick={() => setRelocateMode(false)}
            >
              Cancel
            </button>
          </div>
        )}

        <div style={styles.separator} />

        {/* Signage List */}
        <div style={{ marginBottom: 8, fontSize: 11, color: '#999' }}>
          {signages.length} signage(s)
        </div>
        {signages.map((s, idx) => (
          <div
            key={`${s.id}_${idx}`}
            style={{
              ...styles.signageItem,
              ...(selectedId === s.id ? styles.signageItemActive : {}),
            }}
            onClick={() => setSelectedId(selectedId === s.id ? null : s.id)}
          >
            <div style={{ fontSize: 12, fontWeight: 600 }}>
              {/* Editor list always renders names in English, regardless
                  of the app-wide UI language. */}
              {s.label?.en || s.label?.mr || s.label?.hi || s.id}
            </div>
            <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
              {s.type} · ({s.position?.x?.toFixed(1)}, {s.position?.z?.toFixed(1)}) · rot {s.rotation?.y ?? 0}°
            </div>
          </div>
        ))}

        {/* Edit Panel */}
        {selectedSignage && (
          <>
            <div style={styles.separator} />
            <div style={{ fontSize: 12, fontWeight: 700, color: '#FFD700', marginBottom: 6 }}>
              Edit: {selectedSignage.id}
            </div>

            {/* Type */}
            <label style={styles.label}>Type</label>
            <select
              style={styles.select}
              value={selectedSignage.type || 'arrow_text'}
              onChange={e => handleUpdate('type', e.target.value)}
            >
              {SIGNAGE_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>

            {/* Labels with language tabs */}
            <label style={styles.label}>Label</label>
            <div style={{ ...styles.row, marginBottom: 4 }}>
              {LANGUAGES.map(lang => (
                <button
                  key={lang}
                  style={{
                    ...styles.langTab,
                    ...(editLang === lang ? styles.langTabActive : {}),
                  }}
                  onClick={() => setEditLang(lang)}
                >
                  {LANG_LABELS[lang]}
                </button>
              ))}
            </div>
            <textarea
              style={{ ...styles.input, minHeight: 50, resize: 'vertical', fontFamily: 'inherit' }}
              value={selectedSignage.label?.[editLang] || ''}
              onChange={e => handleUpdate(`label.${editLang}`, e.target.value)}
              placeholder={`Label (${editLang}) — use Enter for new line`}
              rows={2}
            />

            {/* Position */}
            <label style={styles.label}>Position</label>
            <div style={styles.row}>
              {['x', 'y', 'z'].map(axis => (
                <div key={axis} style={{ flex: 1 }}>
                  <span style={{ fontSize: 10, color: '#888' }}>{axis.toUpperCase()}</span>
                  <input
                    style={styles.input}
                    type="number"
                    step="0.1"
                    value={selectedSignage.position?.[axis] ?? 0}
                    onChange={e => handleUpdate(`position.${axis}`, e.target.value)}
                  />
                </div>
              ))}
            </div>

            {/* Rotation Y */}
            <label style={styles.label}>Rotation (degrees)</label>
            <input
              style={styles.input}
              type="number"
              step="5"
              value={selectedSignage.rotation?.y ?? 0}
              onChange={e => handleUpdate('rotation.y', e.target.value)}
            />

            {/* Scale */}
            <label style={styles.label}>Scale</label>
            <input
              style={styles.input}
              type="number"
              step="0.1"
              min="0.1"
              max="5"
              value={selectedSignage.scale ?? 1.0}
              onChange={e => handleUpdate('scale', parseFloat(e.target.value) || 1)}
            />

            {/* Colors */}
            <label style={styles.label}>Colors</label>
            <div style={styles.row}>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 10, color: '#888' }}>Arrow</span>
                <input
                  type="color"
                  value={selectedSignage.arrowColor || '#FFD700'}
                  onChange={e => handleUpdate('arrowColor', e.target.value)}
                  style={{ width: '100%', height: 28, border: 'none', cursor: 'pointer' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 10, color: '#888' }}>Text</span>
                <input
                  type="color"
                  value={selectedSignage.textColor || '#FFFFFF'}
                  onChange={e => handleUpdate('textColor', e.target.value)}
                  style={{ width: '100%', height: 28, border: 'none', cursor: 'pointer' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 10, color: '#888' }}>BG</span>
                <input
                  type="color"
                  value={selectedSignage.bgColor || '#000000'}
                  onChange={e => handleUpdate('bgColor', e.target.value)}
                  style={{ width: '100%', height: 28, border: 'none', cursor: 'pointer' }}
                />
              </div>
            </div>

            {/* BG Opacity */}
            <label style={styles.label}>BG Opacity</label>
            <input
              style={styles.input}
              type="number"
              step="0.1"
              min="0"
              max="1"
              value={selectedSignage.bgOpacity ?? 0.7}
              onChange={e => handleUpdate('bgOpacity', parseFloat(e.target.value) || 0)}
            />

            <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
              <button
                style={{ ...styles.btn, flex: 1, background: '#2a7fff', color: '#fff' }}
                onClick={() => { setRelocateMode(!relocateMode); setAddMode(false); }}
              >
                {relocateMode ? '✕ Cancel Move' : '📍 Relocate'}
              </button>
              <button
                style={{ ...styles.btn, ...styles.btnDanger, flex: 1 }}
                onClick={handleDelete}
              >
                🗑️ Delete
              </button>
            </div>
          </>
        )}
      </div>

      {/* Toast */}
      {toast && <div style={styles.toast}>{toast}</div>}
    </>
  );
};

export default SignageEditor;
